import { LockOpenOutlined, LockOutlined } from "@mui/icons-material";
import { Box, css, styled, useTheme } from "@mui/material";
import type { ReadonlyQuat, ReadonlyVec2, ReadonlyVec3 } from "gl-matrix";
import { vec2, vec3 } from "gl-matrix";
import type { MutableRefObject, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { Canvas2D } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useLastPickSample } from "contexts/lastPickSample";
import { drawPart, getCameraState } from "features/engine2D";
import { CameraType, Picker, selectCamera, selectClippingPlanes, selectPicker, selectViewMode } from "features/render";
import { findCameraAlignedPlaneInAppState } from "features/render/utils";
import { processDrawProductTextTemplates } from "features/uom/draw";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { selectIsProcoreDesign, selectNewDesign } from "slices/explorer";
import { ViewMode } from "types/misc";
import { intersectRayWithPlane } from "utils/math";
import { createLatestGate, queryView } from "utils/viewQueries";

import {
    selectDraggingLaser,
    selectHideGizmo,
    selectLockedRotation,
    selectLockRotation,
    selectOutlineLaser3d,
} from "./outlineLaserSlice";
import { getLaserGizmoAlignmentRotation, getLaserGizmoCoords } from "./utils";

export function OutlineLaserGizmoCanvas({
    renderFnRef,
}: {
    renderFnRef: MutableRefObject<((moved: boolean) => void) | undefined>;
}) {
    const theme = useTheme();
    const {
        state: { size, view, canvas: mainCanvas },
    } = useExplorerGlobals();
    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null | undefined>(null);
    const lastPickSample = useLastPickSample();
    const isOutlineLaserPicker = useAppSelector(selectPicker) === Picker.OutlineLaser;
    const laser3d = useAppSelector(selectOutlineLaser3d);
    const dirty = useRef(false);
    const cameraState = useAppSelector(selectCamera);
    const newDesign = useAppSelector(selectNewDesign);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const lockRotation = useAppSelector(selectLockRotation);
    const lockedRotation = useAppSelector(selectLockedRotation);
    const draggingLaser = useAppSelector(selectDraggingLaser);
    const hideGizmo = useAppSelector(selectHideGizmo);
    const clipping = useAppSelector(selectClippingPlanes);
    const labelContainerRef = useRef<HTMLDivElement>(null);
    const isCrossSection = useAppSelector(selectViewMode) === ViewMode.CrossSection;

    const [pointerPos, setPointerPos] = useState<ReadonlyVec2 | null>(null);
    const [cursorPos, setCursorPos] = useState<ReadonlyVec2 | null>(null);
    const [iconPos, setIconPos] = useState<ReadonlyVec2 | null>(null);
    const lastIconPosQuad = useRef(0);

    const uom = useUom();

    const cameraAlignedPlane = useMemo(
        () => findCameraAlignedPlaneInAppState(clipping, cameraState),
        [cameraState, clipping],
    );

    // true - Use clipping plane position
    // false - Use pick position
    const usePointerPos = Boolean(isCrossSection && cameraAlignedPlane && cameraState.type === CameraType.Orthographic);

    useEffect(() => {
        function onPointerMove(event: PointerEvent) {
            setPointerPos([event.offsetX, event.offsetY]);
        }

        if (!mainCanvas || !usePointerPos) {
            setPointerPos(null);
            return;
        }

        mainCanvas.addEventListener("pointermove", onPointerMove);
        return () => {
            mainCanvas.removeEventListener("pointermove", onPointerMove);
        };
    }, [mainCanvas, usePointerPos]);

    const drawGate = useRef(createLatestGate()).current;

    const draw = useCallback(async () => {
        if (!view?.measure || !ctx || !canvas) {
            return;
        }

        const ticket = drawGate.begin();
        const clear = () => {
            if (dirty.current) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                labelContainerRef.current?.querySelectorAll("[data-axis]").forEach((node) => {
                    (node as HTMLElement).style.display = "none";
                });
            }
        };

        let normal: ReadonlyVec3 | null = null;
        let pos: ReadonlyVec3 | null = null;
        if (cameraAlignedPlane && usePointerPos) {
            const { normalOffset } = cameraAlignedPlane;
            normal = vec3.fromValues(normalOffset[0], normalOffset[1], normalOffset[2]);
            if (!pointerPos) {
                drawGate.cancel(ticket);
                clear();
                return;
            }
            pos = view.convert.screenSpaceToWorldSpace([pointerPos])[0];
            if (!pos) {
                drawGate.cancel(ticket);
                clear();
                return;
            }
        } else if (lastPickSample) {
            normal = lastPickSample.normal;
            pos = lastPickSample.position;
        }

        if (!pos || !normal || hideGizmo) {
            drawGate.cancel(ticket);
            clear();
            setIconPos(null);
            return;
        }

        let gizmoCoords = getLaserGizmoCoords(pos, normal, undefined);

        if (lockedRotation) {
            const { xDir, yDir } = gizmoCoords;
            const gizmoRotation = adjustGizmoRotation(normal, lockedRotation, xDir, yDir);
            gizmoCoords = getLaserGizmoCoords(pos, normal, gizmoRotation);
        }
        let { x0, x1, y0, y1, z0, z1, xDir, yDir } = gizmoCoords;

        const outlineIntersection = await queryView(view, "screenSpaceLaser", {
            laserPosition: pos,
            onlyOnOutlines: usePointerPos,
            xDir,
            yDir,
            zDir: normal,
            autoAlign: lockedRotation === null,
        });
        if (!drawGate.accept(ticket)) {
            return;
        }
        clear();
        const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
        let foundX = false;
        let foundY = false;
        let foundZ = false;

        let iconX0 = x0;
        if (outlineIntersection) {
            const l0 = outlineIntersection.left?.[0];
            const r0 = outlineIntersection.right?.[0];
            if (l0 && r0) {
                // if l0/r0 are swapped relative to x0/x1 - try to keep icon on one side
                iconX0 = vec3.dot(x0, l0) > vec3.dot(x1, l0) ? l0 : r0;

                x0 = l0;
                x1 = r0;
                foundX = true;
                xDir = vec3.sub(xDir, x1, x0);
                vec3.normalize(xDir, xDir);
            }

            const u0 = outlineIntersection.up?.[0];
            const d0 = outlineIntersection.down?.[0];
            if (u0 && d0) {
                y0 = u0;
                y1 = d0;
                foundY = true;
                yDir = vec3.sub(yDir, y1, y0);
                vec3.normalize(yDir, yDir);
            }

            if (foundX && !foundY) {
                yDir = vec3.cross(yDir, normal, xDir);
                y0 = vec3.add(y0, pos, yDir);
                y1 = vec3.scaleAndAdd(y1, pos, yDir, -1);
            } else if (!foundX && foundY) {
                xDir = vec3.cross(xDir, normal, vec3.negate(vec3.create(), yDir));
                x0 = vec3.add(x0, pos, xDir);
                x1 = vec3.scaleAndAdd(x1, pos, xDir, -1);
            }

            const zu0 = outlineIntersection.zUp?.[0];
            const zd0 = outlineIntersection.zDown?.[0];
            if (zu0 && zd0) {
                z0 = zu0;
                z1 = zd0;
                foundZ = true;
            }
        }

        const camera = getCameraState(view.renderState.camera);
        const pos2d = toScreenSpace(pos);
        if (!pos2d) {
            return;
        }
        for (const [axis, p1, p2, found, color] of [
            ...(!draggingLaser || draggingLaser.measurementX ? [["x", x0, x1, foundX, "#00FF33"]] : []),
            ...(!draggingLaser || draggingLaser.measurementY ? [["y", y0, y1, foundY, "#00FF33"]] : []),
            ...(laser3d && (!draggingLaser || draggingLaser.measurementZ) ? [["z", z0, z1, foundZ, "#00FF33"]] : []),
        ] as [string, vec3, vec3, boolean, string][]) {
            const drawProduct = view.measure?.draw.getDrawObjectFromPoints([p1, p2], {
                closed: false,
                angles: false,
                generateLengthLabels: false,
            });
            if (drawProduct) {
                processDrawProductTextTemplates(uom, drawProduct);
            }
            for (const obj of drawProduct?.objects || []) {
                for (const part of obj.parts) {
                    dirty.current = true;
                    drawPart(
                        ctx,
                        camera,
                        part,
                        { lineColor: "rgba(255, 255, 255, 0.2)" },
                        4,
                        found ? { type: "center" } : undefined,
                    );
                    drawPart(ctx, camera, part, { lineColor: color }, 2, found ? { type: "center" } : undefined);
                }
            }

            // Label
            const node = labelContainerRef.current?.querySelector(`[data-axis="${axis}"]`) as HTMLElement;
            if (!node) {
                continue;
            }
            const mid = vec3.lerp(vec3.create(), p1, p2, 0.5);
            let mid2d = toScreenSpace(mid);
            if (mid2d && found) {
                if (vec2.distance(pos2d, mid2d) < 20) {
                    vec3.lerp(mid, p1, p2, 0.75);
                    mid2d = toScreenSpace(mid) ?? mid2d;
                }
                node.style.left = `${mid2d[0]}px`;
                node.style.top = `${mid2d[1]}px`;
                node.style.display = "block";
                const length = vec3.distance(p1, p2);
                node.textContent = convertAndFormat(uom, length, UnitType.length, { decimals: 3 });
            } else {
                node.style.display = "none";
            }
        }

        const iconYDir = vec3.cross(vec3.create(), normal, vec3.sub(vec3.create(), iconX0, pos));
        const iconY0 = vec3.add(vec3.create(), pos, iconYDir);
        const x2d = toScreenSpace(iconX0);
        const y2d = toScreenSpace(iconY0);
        if (!x2d || !y2d) {
            return;
        }
        setCursorPos(pos2d);

        const x2dDir = vec2.sub(vec2.create(), x2d, pos2d);
        vec2.normalize(x2dDir, x2dDir);
        const y2dDir = vec2.sub(vec2.create(), y2d, pos2d);
        vec2.normalize(y2dDir, y2dDir);
        const y2dDirInv = vec2.negate(vec2.create(), y2dDir);
        const iconPosAngleDiff = vec2.angle(x2dDir, y2dDir) - vec2.angle(x2dDir, y2dDirInv);
        let iconPosQuad = 0;
        if (Math.abs(iconPosAngleDiff) < 10) {
            iconPosQuad = lastIconPosQuad.current;
        } else {
            iconPosQuad = iconPosAngleDiff > 0 ? 0 : 1;
        }
        lastIconPosQuad.current = iconPosQuad;
        const iconPos = vec2.lerp(vec2.create(), x2dDir, iconPosQuad === 0 ? y2dDir : y2dDirInv, 0.5);
        vec2.normalize(iconPos, iconPos);
        vec2.scale(iconPos, iconPos, 20);
        setIconPos(iconPos);
    }, [
        ctx,
        canvas,
        view,
        lastPickSample,
        laser3d,
        lockedRotation,
        uom,
        draggingLaser,
        hideGizmo,
        usePointerPos,
        pointerPos,
        cameraAlignedPlane,
        drawGate,
    ]);

    useEffect(() => {
        void draw();
    }, [draw]);

    useEffect(() => {
        renderFnRef.current = animate;
        return () => (renderFnRef.current = undefined);
        function animate(moved: boolean) {
            if (view && moved) {
                void draw();
            }
        }
    }, [draw, renderFnRef, view]);

    let lockIcon: ReactNode | null = null;
    if (lockRotation && cursorPos && iconPos && !pointerPos) {
        const translate = `${iconPos[0]}px ${iconPos[1]}px`;
        lockIcon = (
            <Box
                sx={{
                    translate: "-50% -50%",
                    pointerEvents: "none",
                    left: `${cursorPos[0]}px`,
                    top: `${cursorPos[1]}px`,
                    position: "absolute",
                    color: lockedRotation ? "#039BE5" : theme.palette.grey[800],
                }}
            >
                {lockedRotation ? <LockOutlined sx={{ translate }} /> : <LockOpenOutlined sx={{ translate }} />}
            </Box>
        );
    }

    return isOutlineLaserPicker && (newDesign || isProcoreDesign) ? (
        <>
            <Canvas2D
                id="outline-laser-gizmo-canvas"
                data-include-snapshot
                ref={(el) => {
                    setCanvas(el);
                    setCtx(el?.getContext("2d") ?? null);
                }}
                width={size.width}
                height={size.height}
            />
            {lockIcon}
            <Box ref={labelContainerRef}>
                {["x", "y", "z"].map((axis) => (
                    <Label key={axis} data-axis={axis}></Label>
                ))}
            </Box>
        </>
    ) : null;
}

const Label = styled(Box)(
    () => css`
        border-radius: 4px;
        pointer-events: none;
        position: absolute;
        translate: -50% -50%;
        padding: 0px 4px;
        background: #000000;
        color: #ffffff;
        font-weight: 600;
        font-size: 12px;
        whitespace: nowrap;
    `,
);

/**
 * Take locked rotation and produce rotation for the new normal that
 * resembles the locked rotation as much as possible.
 *
 * Idea is to make a ray for each of the vector, that points in the direction of an axis plain
 * aligned the most with the normal (means highest dot product).
 * Max alignment guarantees there will be intersection and the error will be minimal.
 *
 * Just projecting a point on the plane wasn't giving good results.
 *
 * @param normal new normal
 * @param lockedRotation locked rotation info
 * @returns rotation for the new normal
 */
function adjustGizmoRotation(
    normal: ReadonlyVec3,
    {
        intersection: { right, left, up, down },
        normal: lockNormal,
        rotation: lockRotation,
        pos: lockPos,
    }: NonNullable<ReturnType<typeof selectLockedRotation>>,
    xDir: ReadonlyVec3,
    yDir: ReadonlyVec3,
): ReadonlyQuat {
    if (Math.abs(vec3.dot(normal, lockNormal)) > 0.999) {
        return lockRotation;
    }

    const mostAlignedAxis = findMostAlignedAxisPlane(normal);
    const intersect = (p: ReadonlyVec3) => intersectRayWithPlane(p, mostAlignedAxis, normal, 0)!;

    const newXDir = intersect(xDir);
    vec3.normalize(newXDir, newXDir);

    const newYDir = intersect(yDir);
    vec3.normalize(newYDir, newYDir);

    const newLeft = vec3.create();
    const newRight = vec3.create();
    const newUp = vec3.create();
    const newDown = vec3.create();

    if (left || right) {
        if (left) {
            const intersection = intersect(vec3.sub(vec3.create(), left, lockPos));
            vec3.normalize(newLeft, intersection);
            vec3.negate(newRight, newLeft);
        } else if (right) {
            const intersection = intersect(vec3.sub(vec3.create(), right, lockPos));
            vec3.normalize(newRight, intersection);
            vec3.negate(newLeft, newRight);
        }
        vec3.cross(newUp, normal, newLeft);
        vec3.negate(newDown, newUp);
    } else if (up || down) {
        if (up) {
            const intersection = intersect(vec3.sub(vec3.create(), up, lockPos));
            vec3.normalize(newUp, intersection);
            vec3.negate(newDown, newUp);
        } else if (down) {
            const intersection = intersect(vec3.sub(vec3.create(), down, lockPos));
            vec3.normalize(newDown, intersection);
            vec3.negate(newUp, newDown);
        }
        vec3.cross(newLeft, normal, newUp);
        vec3.negate(newRight, newLeft);
    } else {
        return lockRotation;
    }

    vec3.add(newLeft, newLeft, lockPos);
    vec3.add(newRight, newRight, lockPos);
    vec3.add(newUp, newUp, lockPos);
    vec3.add(newDown, newDown, lockPos);

    const result =
        getLaserGizmoAlignmentRotation(
            {
                left: [newLeft],
                right: [newRight],
                up: [newUp],
                down: [newDown],
            },
            lockPos,
            newXDir,
            newYDir,
        ) ?? lockRotation;
    return result;
}

function findMostAlignedAxisPlane(normal: ReadonlyVec3) {
    const planes = [vec3.fromValues(1, 0, 0), vec3.fromValues(0, 1, 0), vec3.fromValues(0, 0, 1)];
    let maxDot = 0;
    let maxIndex = 0;
    for (let i = 0; i < planes.length; ++i) {
        const dot = Math.abs(vec3.dot(normal, planes[i]));
        if (dot > maxDot) {
            maxDot = dot;
            maxIndex = i;
        }
    }
    return planes[maxIndex];
}
